// [review-compact] Word-level diff between the draft clause and the certified
// replacement, for the card's inline "~~2nd~~ 4th" rendering. Client-only, pure.
// LCS over word tokens; returns null when a diff would not help the reader (huge
// texts, or two clauses that share almost no wording) so the caller can fall back
// to the plain draft text.
export type DiffSeg = { text: string; kind: "same" | "del" | "ins" };

const MAX_CELLS = 90_000; // 300 × 300 words
const MIN_SHARED = 0.35; // shared words / longer side

export function wordDiff(from: string, to: string): DiffSeg[] | null {
  const a = from.split(/\s+/).filter(Boolean);
  const b = to.split(/\s+/).filter(Boolean);
  if (a.length === 0 || b.length === 0) return null;
  if (a.length * b.length > MAX_CELLS) return null;

  // dp[i][j] = LCS length of a[i..] and b[j..]
  const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0)
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i]![j] =
        a[i] === b[j]
          ? dp[i + 1]![j + 1]! + 1
          : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  if (dp[0]![0]! / Math.max(a.length, b.length) < MIN_SHARED) return null;

  const segs: DiffSeg[] = [];
  const push = (kind: DiffSeg["kind"], word: string): void => {
    const last = segs[segs.length - 1];
    if (last !== undefined && last.kind === kind) last.text += ` ${word}`;
    else segs.push({ text: word, kind });
  };
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      push("same", a[i]!);
      i++;
      j++;
    } else if (j >= b.length || (i < a.length && dp[i + 1]![j]! >= dp[i]![j + 1]!)) {
      push("del", a[i]!);
      i++;
    } else {
      push("ins", b[j]!);
      j++;
    }
  }
  // A removal reads before its replacement ("2nd 4th"): within a change run,
  // order deletions first.
  for (let k = 0; k + 1 < segs.length; k++) {
    if (segs[k]!.kind === "ins" && segs[k + 1]!.kind === "del") {
      [segs[k], segs[k + 1]] = [segs[k + 1]!, segs[k]!];
    }
  }
  return segs;
}
